import { useEffect, useState } from "react";
import { Send } from "lucide-react";
import api from "../../lib/api";
import { Card, Badge } from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";

export default function Marketing() {
  const [blasts, setBlasts] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: "", body: "", channel: "email", client_tag_id: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);

  const load = () =>
    api
      .get("/owner/message-blasts")
      .then(({ data }) => setBlasts(data))
      .catch(() => setBlasts([]))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
    api.get("/owner/client-tags").then(({ data }) => setTags(data)).catch(() => setTags([]));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setSent(null);
    try {
      const { data } = await api.post("/owner/message-blasts", {
        ...form,
        client_tag_id: form.client_tag_id || null,
      });
      setSent(data.recipient_count);
      setForm({ title: "", body: "", channel: "email", client_tag_id: "" });
      load();
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <h1 className="mb-2 font-montserrat text-2xl font-bold text-slate-900">Marketing</h1>
      <p className="mb-6 text-sm text-muted-foreground">
        Send an announcement, promotion, or reminder to your client base. Delivered as an in-app/push
        notification today — plug in a live email or SMS provider later without changing this flow.
      </p>

      <Card className="mb-6 p-4">
        <form onSubmit={submit} className="space-y-3">
          <Input placeholder="Title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <textarea
            placeholder="Message"
            required
            rows={4}
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            className="w-full rounded-xl border border-border p-3 text-sm outline-none focus:border-primary"
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <select
              value={form.channel}
              onChange={(e) => setForm({ ...form, channel: e.target.value })}
              className="h-11 rounded-xl border border-input bg-white px-4 text-sm outline-none focus:border-primary"
            >
              <option value="email">Email</option>
              <option value="sms">SMS</option>
              <option value="both">Email + SMS</option>
            </select>
            <select
              value={form.client_tag_id}
              onChange={(e) => setForm({ ...form, client_tag_id: e.target.value })}
              className="h-11 rounded-xl border border-input bg-white px-4 text-sm outline-none focus:border-primary"
            >
              <option value="">All clients</option>
              {tags.map((t) => (
                <option key={t.id} value={t.id}>Tag: {t.name}</option>
              ))}
            </select>
          </div>
          <Button type="submit" disabled={sending}>
            <Send className="h-4 w-4" /> {sending ? "Sending…" : "Send now"}
          </Button>
          {sent !== null && <p className="text-sm text-secondary">Sent to {sent} client{sent === 1 ? "" : "s"}.</p>}
        </form>
      </Card>

      <h2 className="mb-3 font-montserrat text-lg font-bold text-slate-900">Sent history</h2>
      {loading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : blasts.length === 0 ? (
        <p className="text-muted-foreground">No messages sent yet.</p>
      ) : (
        <div className="divide-y divide-border rounded-2xl border border-border">
          {blasts.map((b) => (
            <div key={b.id} className="p-4">
              <div className="flex items-center justify-between">
                <p className="font-medium text-slate-900">{b.title}</p>
                <Badge>{b.recipient_count} recipients</Badge>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">{b.body}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {b.channel} · {b.client_tag?.name ? `Tag: ${b.client_tag.name}` : "All clients"} ·{" "}
                {new Date(b.sent_at).toLocaleString()}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
